import { Handle, type Node, type NodeProps, Position } from "@xyflow/react";

import { Badge, Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui";
import { useDipLabels } from "~/features/dataImportProjects/hooks/useDipLabels";
import type { DipItemNodeData, DipTone } from "~/features/dataImportProjects/types";
import { badgeToneFor, countToneClass, squareToneClass } from "~/features/dataImportProjects/utils/tone";
import { cn } from "~/lib/utils";

const NODE_KIND_CLASS: Record<NonNullable<DipItemNodeData["nodeKind"]>, string> = {
  dataSource: "border-score-b bg-score-b-bg",
  batchChangeRequestsJob: "border-border-strong bg-bg-subtle shadow-none",
  batchEvidencesJob: "border-border-strong bg-bg-subtle shadow-none",
  changeRequests: "border-[#B9D8F3] bg-[#F3F8FE]",
  composer: "border-border-strong bg-white shadow-none",
  evidenceImporter: "border-border-strong bg-white shadow-none",
  evidenceDrafts: "border-[#A7D8BE] bg-[#F5FBF8]",
  evidenceSet: "border-score-a bg-score-a-bg",
  records: "border-primary-300 bg-primary-50",
};

const NODE_VARIANT_CLASS: Partial<
  Record<NonNullable<DipItemNodeData["nodeKind"]>, Record<NonNullable<DipItemNodeData["nodeVariant"]>, string>>
> = {
  changeRequests: {
    entity: "border-[#B9D8F3] bg-[#F3F8FE]",
    relation: "border-[#B9D8F3]",
    record_update: "border-[#B9D8F3]",
  },
  records: {
    entity: "border-primary-300 bg-primary-50",
    relation: "border-primary-300",
    record_update: "border-primary-300",
  },
};

const COMPACT_NODE_KINDS = new Set<DipItemNodeData["nodeKind"]>(["evidenceDrafts", "changeRequests"]);
const STATUS_BADGE_CLASS = "border border-current";

const STAT_TONE_CLASS: Record<DipTone, string> = {
  neutral: "text-fg-3",
  success: "text-score-a",
  attention: "text-[#B25612]",
  error: "text-score-c",
  primary: "text-primary-700",
  accent: "text-[#5E3884]",
};

export function DipItemNode({ data }: NodeProps<Node<DipItemNodeData>>) {
  const labels = useDipLabels();
  const clickable = (typeof data.detailPath === "string" && data.detailPath.length > 0) || data.peek != null;
  const showHandles = data.showHandles ?? true;
  const isCompact = COMPACT_NODE_KINDS.has(data.nodeKind);
  const handleClassName = cn("!h-2 !w-2 !border-none !bg-primary-300", !showHandles && "!opacity-0");
  const nodeVariantClass = data.nodeVariant ? NODE_VARIANT_CLASS[data.nodeKind]?.[data.nodeVariant] : undefined;
  const nodeStyle = data.nodeVariant === "relation" ? { backgroundColor: "#fff" } : undefined;
  return (
    <>
      <Handle type="target" position={Position.Left} className={handleClassName} />
      <div
        className={cn(
          "flex h-full items-center justify-between rounded-[var(--radius-md)] border border-border-default bg-bg-surface shadow-sm transition-colors",
          isCompact ? "w-[228px] gap-1.5 px-2 py-2" : "w-[380px] gap-2.5 px-3 py-2.5",
          nodeVariantClass ?? NODE_KIND_CLASS[data.nodeKind],
          data.isDeactivated && "bg-bg-subtle opacity-60",
          data.isDisabled && "bg-bg-subtle opacity-60",
          clickable && "cursor-pointer hover:border-primary-500 hover:bg-primary-50",
        )}
        style={nodeStyle}
      >
        <div className={cn("flex min-w-0 items-center", isCompact ? "gap-1.5" : "gap-2")}>
          {data.count !== null && (
            <span
              className={cn(
                "grid shrink-0 place-items-center rounded-full border",
                isCompact ? "h-7 w-7 text-[11px] font-bold" : "h-[34px] w-[34px] text-12-bold",
                countToneClass[data.countTone],
              )}
            >
              {data.count}
            </span>
          )}
          {data.squareTone && (
            <span
              className={cn(
                "shrink-0 rounded-[3px]",
                isCompact ? "h-2.5 w-2.5" : "h-3 w-3",
                squareToneClass[data.squareTone],
              )}
            />
          )}
          <span className="min-w-0">
            <strong className={cn("line-clamp-2 block text-fg-1", isCompact ? "text-12-bold" : "text-14-bold")}>
              {data.title}
            </strong>
            {data.subtitle && <span className="line-clamp-2 block text-12-regular text-fg-3">{data.subtitle}</span>}
          </span>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          {data.rightStats && data.rightStats.length > 0 && (
            <div
              className={cn("flex flex-col items-end gap-0.5 leading-none", isCompact ? "text-[10px]" : "text-[11px]")}
            >
              {data.rightStats.map((stat) => (
                <span key={stat.labelKey} className={cn("whitespace-nowrap font-medium", STAT_TONE_CLASS[stat.tone])}>
                  <span className="font-bold">{stat.value}</span> {labels[stat.labelKey] ?? stat.labelKey}
                </span>
              ))}
            </div>
          )}
          <div className="flex flex-wrap justify-end gap-1">
            {data.badges.map((badge) => (
              <Badge key={badge.labelKey} tone={badgeToneFor[badge.tone]} className={STATUS_BADGE_CLASS}>
                {labels[badge.labelKey] ?? badge.labelKey}
              </Badge>
            ))}
            {data.pendingCorrections != null && data.pendingCorrections > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge tone={badgeToneFor.attention} className={STATUS_BADGE_CLASS}>
                    {labels["status.pendingCorrections"]} {data.pendingCorrections}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>{labels["status.pendingCorrectionsTooltip"]}</TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>
      </div>
      <Handle type="source" position={Position.Right} className={handleClassName} />
    </>
  );
}
